import {useCallback, useEffect, useState, useSyncExternalStore} from 'react'
import {getMe} from '../api/generated/endpoints/auth/auth'
import type {UserResponse} from '../api/generated/model/userResponse'
import {reconcileWithMe} from '../cookieConsent/sync'
import {setGaUserProperties} from '../analytics/ga'

// Module-scope cache so multiple consumers (header, account page) share one fetch.
let cachedMe: UserResponse | null = null
let inFlight: Promise<UserResponse> | null = null
type Listener = () => void
const listeners = new Set<Listener>()

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = (): UserResponse | null => cachedMe

const bucketAccountAge = (createdAt: string): string => {
  const ageMs = Date.now() - new Date(createdAt).getTime()
  const ageDays = ageMs / (1000 * 60 * 60 * 24)
  if (ageDays < 1) return '<1'
  if (ageDays < 7) return '1-7'
  if (ageDays < 30) return '7-30'
  return '30+'
}

const setCache = (next: UserResponse | null): void => {
  if (cachedMe === next) return
  cachedMe = next
  if (next) {
    setGaUserProperties({
      has_credits: next.credits > 0,
      account_age_days: bucketAccountAge(next.createdAt),
    })
  }
  for (const l of listeners) l()
}

export const fetchMe = async (): Promise<UserResponse> => {
  if (inFlight) return inFlight
  inFlight = getMe()
    .then((res) => {
      setCache(res.data)
      reconcileWithMe(res.data)
      return res.data
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

interface UseMeResult {
  me: UserResponse | null
  loading: boolean
  error: Error | null
  refresh: () => Promise<UserResponse | null>
  /** Replace the cached user (e.g. after a PATCH/POST that returns the fresh user). */
  setMe: (me: UserResponse) => void
}

/**
 * Returns the current DB user (from `GET /auth/me`).
 * Caches the result module-wide and subscribes the calling component via
 * useSyncExternalStore, so PublicLayout's header avatar and AccountPage stay
 * in sync without refetching — and the title always re-renders when setMe()
 * pushes a fresh response into the cache.
 */
export const useMe = (): UseMeResult => {
  const me = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const [error, setError] = useState<Error | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (cachedMe) return
    let cancelled = false
    fetchMe().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)))
    })
    return () => {
      cancelled = true
    }
  }, [])

  const refresh = useCallback(async (): Promise<UserResponse | null> => {
    setRefreshing(true)
    setError(null)
    try {
      return await fetchMe()
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      return null
    } finally {
      setRefreshing(false)
    }
  }, [])

  const setMe = useCallback((next: UserResponse) => {
    setCache(next)
  }, [])

  const loading = me === null && error === null
  return {me, loading: loading || refreshing, error, refresh, setMe}
}

/** Reset the cached user (e.g. on sign-out). Components subscribed via useMe will see null. */
export const resetMeCache = (): void => {
  setCache(null)
}
